import React from "react";
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";

type Props = {
  open: boolean;
  salesPerson: SalesPeople | null;
  onClose: () => void;
};

const useStyles = makeStyles(() => ({
  avatarLarge: {
    width: 72,
    height: 72,
    borderRadius: "50%",
  },
  detailLabel: {
    color: "#637381",
    fontSize: 12,
    marginBottom: 2,
  },
  detailValue: {
    color: "#1C252E",
    fontSize: 14,
    wordBreak: "break-word",
  },
  link: {
    color: "#1976D2",
    textDecoration: "none",
  },
  linksList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
}));

const getStatusColor = (status: SalesPeople["online_status"]): string => {
  switch (status) {
    case "online":
      return "#00AB55";
    case "away":
      return "#FFAB00";
    default:
      return "#FF5630";
  }
};

const SalesPersonDetailDialog: React.FC<Props> = ({
  open,
  salesPerson,
  onClose,
}) => {
  const classes = useStyles();

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      aria-labelledby="salesperson-detail-title"
    >
      <DialogTitle id="salesperson-detail-title">
        {salesPerson
          ? `${salesPerson.first_name} ${salesPerson.last_name}`
          : "Detalles del asesor"}
      </DialogTitle>
      <DialogContent dividers>
        {salesPerson && (
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <Avatar
                className={classes.avatarLarge}
                alt={salesPerson.first_name}
                src={
                  salesPerson.avatar_list?.[0]?.url ||
                  (salesPerson as any).avatar ||
                  undefined
                }
              />
              <div>
                <Typography className={classes.detailLabel}>Estado</Typography>
                <Typography
                  className={classes.detailValue}
                  style={{
                    color: getStatusColor(salesPerson.online_status),
                    textTransform: "capitalize",
                  }}
                >
                  {salesPerson.online_status}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Correo</Typography>
              <Typography className={classes.detailValue}>
                {salesPerson.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Celular</Typography>
              <Typography className={classes.detailValue}>
                {salesPerson.phone}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Cédula</Typography>
              <Typography className={classes.detailValue}>
                {salesPerson.cedula}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Ciudad</Typography>
              <Typography className={classes.detailValue}>
                {salesPerson.city_name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>
                Perfil profesional
              </Typography>
              <Typography className={classes.detailValue}>
                {salesPerson.professional_profile}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Instagram</Typography>
              {salesPerson.instagram ? (
                <a
                  className={classes.link}
                  href={
                    salesPerson.instagram.startsWith("http")
                      ? salesPerson.instagram
                      : `https://instagram.com/${salesPerson.instagram.replace(
                          /^@/,
                          ""
                        )}`
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {salesPerson.instagram}
                </a>
              ) : (
                <Typography className={classes.detailValue}>-</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>
                Video presentación
              </Typography>
              {salesPerson.video_presentation_url ? (
                <a
                  className={classes.link}
                  href={salesPerson.video_presentation_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Ver video
                </a>
              ) : (
                <Typography className={classes.detailValue}>-</Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>Diploma</Typography>
              {salesPerson.cv?.length ? (
                <div className={classes.linksList}>
                  {salesPerson.cv.map((f, idx) => (
                    <a
                      key={f.url}
                      className={classes.link}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Archivo {idx + 1}
                    </a>
                  ))}
                </div>
              ) : (
                <Typography className={classes.detailValue}>-</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.detailLabel}>
                Portafolio
              </Typography>
              {salesPerson.portfolio?.length ? (
                <div className={classes.linksList}>
                  {salesPerson.portfolio.map((f, idx) => (
                    <a
                      key={f.url}
                      className={classes.link}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Imagen {idx + 1}
                    </a>
                  ))}
                </div>
              ) : (
                <Typography className={classes.detailValue}>-</Typography>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesPersonDetailDialog;
